import { formatContractNumber, CONTRACT_FACT_SEPARATOR } from '../constants/contractActions';
import { isRadarSourced } from '../constants/employmentSourceSystems';
import { renewalSwitchDay } from '../constants/employmentEventAnnotations';

/**
 * ADR-171 U4 (ADR-159 §5) — what End Employment does to EACH open contract,
 * shown in the confirmation before the operator submits. Mirrors the server
 * (person.employment-ended.js); the server stays the authority — this is the
 * explanation, not the decision.
 */

const ymd = (value) => (value ? String(value).slice(0, 10) : null);
const dmy = (value) => {
  const v = ymd(value);
  return v ? `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)}` : null;
};

/** One line naming a contract from the GET /employees/:id `contracts` entry. */
export const contractLineLabel = (contract) => {
  const parts = [];
  const number = formatContractNumber(contract?.contractNumber);
  if (number) {
    parts.push(number);
  }
  if (contract?.contractType?.name) {
    parts.push(contract.contractType.name);
  }
  if (contract?.contractCompany?.name) {
    parts.push(contract.contractCompany.name);
  }
  if (contract?.startDate) {
    parts.push(`from ${dmy(contract.startDate)}`);
  }
  return parts.join(CONTRACT_FACT_SEPARATOR) || 'Contract';
};

export const openContractsOf = (employee) =>
  (employee?.contracts || []).filter((c) => !c.endDate);

/** ADR-185 — what RESIGN at D does to one contract, as a code the panels share. */
export const RESIGN_EFFECTS = Object.freeze({
  ENDS_AT: 'ENDS_AT',
  ALREADY_ENDS: 'ALREADY_ENDS',
  VOIDED: 'VOIDED'
});

const resignEffectOf = (contract, D) => {
  const start = ymd(contract.startDate);
  const scheduledEnd = ymd(contract.termEndDate);
  if (D && start >= D) {
    return { effect: RESIGN_EFFECTS.VOIDED };
  }
  if (D && scheduledEnd && scheduledEnd <= D) {
    return { effect: RESIGN_EFFECTS.ALREADY_ENDS, endsOn: scheduledEnd };
  }
  return { effect: RESIGN_EFFECTS.ENDS_AT, endsOn: D };
};

/**
 * ADR-185 D5/D6/§10 — the notes a RESIGN row carries beyond its outcome:
 * the contract's scheduled replacement (cancelled when it would switch after
 * the person has left — person.employment-ended.js; otherwise it switches and
 * the new contract ends on D too — contract.replaced.js) and its ingestion
 * owner.
 */
const resignNotesFor = (contract, D, effect) => {
  const notes = [];
  const cutover = ymd(contract.pendingRenewal?.effectiveOn);
  if (cutover && D && effect !== RESIGN_EFFECTS.VOIDED) {
    const switchDay = dmy(renewalSwitchDay(cutover));
    if (cutover >= D) {
      notes.push({ kind: 'replacement', severity: 'info', text: `Its scheduled replacement (switch on ${switchDay}) is cancelled — it would start after the person has left.` });
    } else {
      // ADR-185 §10 — the replacement switches while the person is still
      // employed; the resignation moves onto the new contract with it.
      notes.push({ kind: 'replacement', severity: 'info', text: `Replaced on ${switchDay}; the new contract also ends on ${dmy(D)}.` });
    }
  }
  if (contract.source && !isRadarSourced({ source: contract.source })) {
    notes.push({ kind: 'source', severity: 'warning', text: `Owned by ${contract.source}: record the departure there too, or its next sync run withdraws this end.` });
  }
  return notes;
};

/**
 * ADR-185 D2 — the server refuses RESIGN when the PRIMARY starts on or after
 * D (person.employment-ended.js, PRIMARY_STARTS_AFTER_DATE). Said before
 * submit instead of after the last step. Only with more than one contract:
 * with one, the date picker's minimum already covers it.
 * @returns {string|null}
 */
export const resignationRefusal = ({ contracts = [], resignationDate = null }) => {
  const D = ymd(resignationDate);
  if (!D || contracts.length < 2) {
    return null;
  }
  const primary = contracts.find((c) => c.isPrimary);
  if (primary && ymd(primary.startDate) >= D) {
    return `The primary contract starts on ${dmy(primary.startDate)}, on or after the resignation date — make the contract the person works under primary first, or use Hire Withdraw.`;
  }
  return null;
};

/**
 * @param {Object} args
 * @param {'RESIGN'|'HIRE_WITHDRAW'|'NO_SHOW'} args.kind
 * @param {Array} args.contracts            the person's open contracts
 * @param {string} [args.resignationDate]   RESIGN only (YYYY-MM-DD)
 * @param {string} [args.today]             YYYY-MM-DD, defaults to now (UTC)
 * @returns {Array<{ id, label, isPrimary, outcome, effect?, endsOn?, notes? }>}  primary first
 */
export const previewEndEmployment = ({ kind, contracts = [], resignationDate = null, today = null }) => {
  const now = today || new Date().toISOString().slice(0, 10);
  const D = ymd(resignationDate);
  const ordered = [...contracts].sort((a, b) => (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1));

  return ordered.map((contract) => {
    const start = ymd(contract.startDate);
    const base = { id: contract.id, label: contractLineLabel(contract), isPrimary: Boolean(contract.isPrimary) };
    if (kind === 'RESIGN') {
      const { effect, endsOn } = resignEffectOf(contract, D);
      let outcome;
      if (!D) {
        outcome = 'Ends on the resignation date';
      } else if (effect === RESIGN_EFFECTS.VOIDED) {
        outcome = `Voided — it would only start on ${dmy(start)}`;
      } else if (effect === RESIGN_EFFECTS.ALREADY_ENDS) {
        outcome = `Keeps its earlier end on ${dmy(endsOn)}`;
      } else {
        outcome = `Ends on ${dmy(D)}`;
      }
      return { ...base, outcome, effect, endsOn: endsOn || null, notes: resignNotesFor(contract, D, effect) };
    }
    let outcome;
    if (kind === 'NO_SHOW') {
      outcome = start <= now ? 'Marked as no-show' : 'Withdrawn — it has not started';
    } else {
      outcome = 'Withdrawn';
    }
    return { ...base, outcome };
  });
};
